/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren } from 'preact';
import type { BundleType } from '#resources/nls/bundle';
import type { ListNoDataVariantOptions } from './themes/ListNoDataStyles.css';
import type { ListNoDataRendererContext } from './List.types';

import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { classNames } from '#utils/UNSAFE_classNames';
import { listRowStyles } from './themes/ListRowStyles.css';
import { ListNoDataRedwoodTheme } from './themes/redwood/ListNoDataTheme';

/**
 * Props for the ListNoData Component
 */
type ListNoDataProps = {
  listId: string;
  isShowFocusRing: boolean;
  hasTabIndex: boolean;
  isTabbableMode: boolean;
  ariaRowIndex: number;
  renderer?: (context: ListNoDataRendererContext) => ComponentChildren;
};

/**
 * The internal component used to render an empty list cell in a ListBody.
 */
export function ListNoData({
  listId,
  isShowFocusRing,
  hasTabIndex,
  isTabbableMode,
  ariaRowIndex,
  renderer
}: ListNoDataProps) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const rowClasses = classNames([listRowStyles.base]);

  const { classes: themeClasses } = useComponentTheme<ListNoDataVariantOptions>(
    ListNoDataRedwoodTheme,
    {
      defaultRenderer: renderer == null ? 'isDefaultRenderer' : 'notDefaultRenderer',
      showFocusRing: isShowFocusRing ? 'isShowFocusRing' : 'notShowFocusRing'
    }
  );

  const cellClasses = classNames([themeClasses]);

  function defaultContentRenderer() {
    return translations.collection_noData();
  }

  const contentRenderer = renderer != null ? renderer : defaultContentRenderer;

  return (
    <div
      aria-rowindex={ariaRowIndex}
      class={rowClasses}
      style={'height:100%;width:100%;'}
      role={'row'}>
      <div
        aria-colindex={1}
        class={cellClasses}
        tabIndex={hasTabIndex ? 0 : -1}
        role={'gridcell'}
        data-oj-cell-type={'noData'}
        data-oj-list-focusable={listId}
        data-oj-list-nodata={listId}>
        {contentRenderer({ isTabbable: isTabbableMode })}
      </div>
    </div>
  );
}
